// Accent-insensitive text compare for client search and dropdown filters.
// The letter list is mirrored in TotalWaterpoloArena.Application/Common/Text/AccentFold.cs.

const PAIRS: Array<[string, string]> = buildPairs();

export function normalizeForSearch(value: string | null | undefined): string {
    if (value == null) {
        return '';
    }

    let text = String(value);
    PAIRS.forEach(([from, to]): void => {
        text = text.split(from).join(to);
    });
    text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return text.toLowerCase().split('\u0131').join('i').trim();
}

export function includesForSearch(
    haystack: string | null | undefined,
    needle: string | null | undefined
): boolean {
    const foldedNeedle = normalizeForSearch(needle);
    if (!foldedNeedle) {
        return true;
    }

    return normalizeForSearch(haystack).includes(foldedNeedle);
}

interface SearchOption {
    children?: unknown;
    label?: unknown;
    props?: { children?: unknown };
}

export function filterOptionForSearch(input: string, option?: SearchOption): boolean {
    if (!normalizeForSearch(input)) {
        return true;
    }

    return includesForSearch(optionSearchText(option), input);
}

function optionSearchText(option?: SearchOption): string {
    if (!option) {
        return '';
    }

    const fromChildren = flattenNode(option.children);
    if (fromChildren) {
        return fromChildren;
    }

    if (typeof option.label === 'string' || typeof option.label === 'number') {
        return String(option.label);
    }

    return flattenNode(option.props ? option.props.children : undefined);
}

function flattenNode(node: unknown): string {
    if (node == null || typeof node === 'boolean') {
        return '';
    }

    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }

    if (Array.isArray(node)) {
        return node.map(flattenNode).join('');
    }

    if (typeof node === 'object' && node !== null && 'props' in node) {
        const props = (node as { props?: { children?: unknown } }).props;
        if (props && props.children != null) {
            return flattenNode(props.children);
        }
    }

    return '';
}

function buildPairs(): Array<[string, string]> {
    const pairs: Array<[string, string]> = [];
    const seen = new Set<string>();

    const add = (from: string, to: string): void => {
        if (!seen.has(from)) {
            seen.add(from);
            pairs.push([from, to]);
        }
    };

    const addBoth = (lowers: string, uppers: string, to: string): void => {
        const lowerChars = Array.from(lowers);
        const upperChars = Array.from(uppers);
        if (lowerChars.length !== upperChars.length) {
            throw new Error(`Accent fold pair length mismatch for '${to}'.`);
        }

        lowerChars.forEach((lower, index): void => {
            add(lower, to);
            add(upperChars[index], to);
        });
    };

    add('ß', 'ss');
    add('ẞ', 'ss');
    add('æ', 'ae');
    add('Æ', 'ae');
    add('œ', 'oe');
    add('Œ', 'oe');
    add('İ', 'i');

    addBoth('àáâãäåāăą', 'ÀÁÂÃÄÅĀĂĄ', 'a');
    addBoth('çćč', 'ÇĆČ', 'c');
    addBoth('èéêëēěę', 'ÈÉÊËĒĚĘ', 'e');
    addBoth('ìíîïīı', 'ÌÍÎÏĪI', 'i');
    addBoth('ñńňņ', 'ÑŃŇŅ', 'n');
    addBoth('òóôõöōőø', 'ÒÓÔÕÖŌŐØ', 'o');
    addBoth('ùúûüūůű', 'ÙÚÛÜŪŮŰ', 'u');
    addBoth('ýÿ', 'ÝŸ', 'y');
    addBoth('ďđ', 'ĎĐ', 'd');
    addBoth('ğģ', 'ĞĢ', 'g');
    addBoth('ķ', 'Ķ', 'k');
    addBoth('ĺľł', 'ĹĽŁ', 'l');
    addBoth('řŕ', 'ŘŔ', 'r');
    addBoth('śşšș', 'ŚŞŠȘ', 's');
    addBoth('ťţț', 'ŤŢȚ', 't');
    addBoth('źżž', 'ŹŻŽ', 'z');

    return pairs;
}
